import React, { useState } from "react";
import {
    ThumbsUp,
    MessageSquare,
    Repeat2,
    Send,
    MoreHorizontal,
    Loader,
} from "lucide-react";
import { Button } from "../../misc/Button";
import { LinkedInPostProps } from "../types";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { Media, MediaType } from "../../../types/media";
import FacebookMediaGalleryOverlay from "../facebook/FacebookMediaGalleryOverlay";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { calculateFittedFontSize } from "../../../utils/autoFitFont";
import PostVideoView from "../PostVideoView";
import { withoutLeadGenerationBody } from "../util/leadGenerationPost";

const LinkedInPost: React.FC<LinkedInPostProps> = ({ post: sourcePost, setLoading, hideDatetime = false, contentCharLimit }) => {
    const post = withoutLeadGenerationBody(sourcePost);
    const [showGallery, setShowGallery] = useState(false);
    const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
    // Toggle now applies to the whole text block including in-post footer
    const [showFullText, setShowFullText] = useState(false);
    const [showComments, setShowComments] = useState(false);

    // Build the combined text from post content and in-post footer (if applicable)
    const rawPostContent: string = Array.isArray(post?.content)
        ? (post?.content as string[]).join("\n\n")
        : (post?.content as string) || "";
    const withinPostContent = contentCharLimit ? rawPostContent.substring(0, contentCharLimit) : rawPostContent;
    const overflowContent = contentCharLimit && rawPostContent.length > contentCharLimit ? rawPostContent.substring(contentCharLimit) : "";
    const inPostFooter =
        post?.footerType === "in-post" && post?.includeFooter && post?.footer
            ? (withinPostContent ? "\n\n" : "") + post.footer
            : "";
    const combinedText = withinPostContent + inPostFooter;

    // Compute a truncated version if the combined text is longer than 150 characters.
    const isTruncated = combinedText.length > 150;
    const truncatedText = isTruncated
        ? combinedText.substring(0, 150) + "..."
        : combinedText;

    const user = useSelector((state: RootState) => state.auth.user);

    // Function to handle media click and open gallery overlay
    const handleMediaClick = (index: number) => {
        setGalleryInitialIndex(index);
        setShowGallery(true);
    };

    const renderMedia = (media: Media[]) => {
        // For Lead Generation posts: if image is generated, display it instead of re-rendering styled block
        // Check if this is a Lead Generation post (either type "Lead Generation" or preview with styling)
        const isLeadGenerationPost = post?.type === 'Lead Generation' || 
                                    (post?.type === 'preview' && post?.background && post?.textColor);

        if (isLeadGenerationPost && media && media.length > 0 && media[0].signedUrl) {
            return (
                <div className="w-full">
                    <img
                        src={media[0].signedUrl}
                        alt="Lead Generation Post"
                        className="w-full h-full object-cover"
                    />
                </div>
            );
        }

        // If backgroundColor and textColor are present, render styled content block instead of media
        if (post?.background && post?.textColor) {
            // Use imageText for the graphic if available, otherwise use content
            const graphicText = (post as any).imageText || post.content;
            const content = (
                typeof graphicText === "string"
                    ? graphicText
                    : Array.isArray(graphicText)
                    ? graphicText.join("\n\n")
                    : ""
            ).trim();

            // Helper function to get background style
            const getBackgroundStyle = (bg: any) => {
                if (!bg) return undefined;
                if (bg.type === 'gradient') {
                    return `linear-gradient(${bg.direction || 'to right'}, ${bg.from || '#000000'}, ${bg.to || '#FFFFFF'})`;
                }
                return bg.color || '#000000';
            };

            // Platform-specific dimensions (matching backend)
            const platformDimensions = { width: 1200, height: 627 }; // LinkedIn dimensions
            const maxWidth = 500;
            const scale = maxWidth / platformDimensions.width;
            const displayWidth = platformDimensions.width * scale;
            const displayHeight = platformDimensions.height * scale;

            if (isMobile) {
                // Mobile: fluid layout — fills container width, locked to 16:9 aspect ratio
                const mobileFontSize = calculateFittedFontSize(content, post.fontSize || 18, post.fontFamily, 320, 167);
                return (
                    <div
                        className="w-full flex items-center justify-center rounded-lg"
                        style={{
                            aspectRatio: "16/9",
                            background: getBackgroundStyle(post.background),
                            color: post.textColor,
                            fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                            padding: "8%",
                        }}
                    >
                        <p
                            className="font-bold text-center break-words"
                            style={{
                                fontSize: `${mobileFontSize}px`,
                                lineHeight: 1.5,
                                wordBreak: "break-word",
                                overflowWrap: "anywhere",
                                width: "100%",
                            }}
                        >
                            {content}
                        </p>
                    </div>
                );
            }

            // Desktop: original pixel-based rendering (unchanged)
            const baseFontSize = post.fontSize || 18;
            const fittedFontSize = calculateFittedFontSize(content, baseFontSize, post.fontFamily, displayWidth, displayHeight);
            const horizontalPadding = Math.round(displayWidth * 0.08);
            const verticalPadding = Math.round(displayHeight * 0.08);

            return (
                <div className="w-full flex justify-center">
                    <div className="flex items-center justify-center rounded-lg"
                         style={{ 
                             background: getBackgroundStyle(post.background), 
                             color: post.textColor,
                             fontFamily: post.fontFamily ? `"${post.fontFamily}", sans-serif` : undefined,
                             width: `${displayWidth}px`,
                             height: `${displayHeight}px`,
                             paddingLeft: `${horizontalPadding}px`,
                             paddingRight: `${horizontalPadding}px`,
                             paddingTop: `${verticalPadding}px`,
                             paddingBottom: `${verticalPadding}px`,
                         }}>
                        <p className="font-bold text-center"
                           style={{
                               fontSize: `${fittedFontSize}px`,
                               lineHeight: 1.5,
                           }}>
                            {content}
                        </p>
                    </div>
                </div>
            );
        }

        const mediaItems = media;
        const count = mediaItems.length;
        const getImageUrl = (m: Media) => (m.signedUrl ? m.signedUrl : m.url);

        // Helper function to render an image or video with click functionality
        const renderImage = (m: Media, altText: string, index: number, inGrid = false) => (
            <div className={`relative overflow-hidden ${inGrid ? "aspect-[4/3]" : "max-h-[500px]"}`} key={index}>
                {m.type === MediaType.ListingVideo ||
                m.type === MediaType.AssetVideo ? (
                    !inGrid ? (
                        <PostVideoView
                            key={getImageUrl(m)}
                            src={getImageUrl(m)}
                            metadata={m.videoMetadata}
                            thumbnail={m.thumbnail}
                            isProcessing={m.isProcessing}
                            onClick={() => handleMediaClick(index)}
                        />
                    ) : (
                        <div className="w-full aspect-[16/9] bg-black overflow-hidden relative">
                            {m.isProcessing ? (
                                <>
                                    {m.thumbnail && (
                                        <img
                                            src={m.thumbnail}
                                            alt="Media"
                                            className="w-full h-full object-contain"
                                        />
                                    )}
                                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded">
                                        <span className="text-white text-sm font-medium">
                                            Video Processing
                                        </span>
                                        <Loader className="ml-2 animate-spin text-secondary" />
                                    </div>
                                </>
                            ) : (
                                <video
                                    key={getImageUrl(m)}
                                    className="w-full h-full object-contain cursor-pointer"
                                    controls
                                    onClick={() => handleMediaClick(index)}
                                >
                                    <source src={getImageUrl(m)} type="video/mp4" />
                                    Your browser does not support the video tag.
                                </video>
                            )}
                        </div>
                    )
                ) : (
                    <img
                        onClick={() => handleMediaClick(index)}
                        src={getImageUrl(m)}
                        alt={altText}
                        className={inGrid ? "absolute inset-0 w-full h-full object-cover cursor-pointer" : "w-full h-auto max-h-[500px] object-contain cursor-pointer"}
                    />
                )}
            </div>
        );

        if (count === 1) {
            return (
                <div className="w-full">
                    {renderImage(mediaItems[0], "Media 1", 0)}
                </div>
            );
        }
        if (count === 2) {
            return (
                <div className="grid grid-cols-2 gap-1">
                    {mediaItems.map((m, index) =>
                        renderImage(m, `Media ${index + 1}`, index, true)
                    )}
                </div>
            );
        }
        if (count === 3) {
            return (
                <div className="grid grid-cols-2 grid-rows-2 gap-1">
                    <div className="row-span-2">
                        {renderImage(mediaItems[0], "Media 1", 0, true)}
                    </div>
                    {mediaItems
                        .slice(1)
                        .map((m, index) =>
                            renderImage(m, `Media ${index + 2}`, index + 1, true)
                        )}
                </div>
            );
        }
        if (count === 4) {
            return (
                <div className="grid grid-cols-2 gap-1">
                    {mediaItems.map((m, index) =>
                        renderImage(m, `Media ${index + 1}`, index, true)
                    )}
                </div>
            );
        }
        if (count > 4) {
            const remaining = count - 4;
            return (
                <div
                    className="grid grid-cols-2 gap-1 hover:cursor-pointer"
                    onClick={() => handleMediaClick(3)}
                >
                    {mediaItems
                        .slice(0, 3)
                        .map((m, index) =>
                            renderImage(m, `Media ${index + 1}`, index, true)
                        )}
                    <div className="relative">
                        <img
                            src={getImageUrl(mediaItems[3])}
                            alt="Media 4"
                            className="w-full h-full object-cover cursor-pointer"
                        />
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                            <span className="text-white text-xl font-semibold">
                                +{remaining}
                            </span>
                        </div>
                    </div>
                </div>
            );
        }
        return null;
    };

    const scheduledDate = post?.scheduledFor
        ? new Date(post.scheduledFor)
        : new Date();
    
    // Calculate time display - for preview mode, show formatted date/time
    let time: string;
    
    // Check if this is a Lead Generation post (for media rendering logic)
    // const isLeadGenerationPost = post?.type === 'Lead Generation' || 
    //                             (post?.type === 'preview' && post?.background && post?.textColor);
    
    if (hideDatetime) {
        time = "";
    } else if (post?.type === 'preview') {
        // Show formatted date/time in preview mode (for ALL post types including Lead Generation)
        const date = new Date(post.scheduledFor);
        const dateString = date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
        });
        const timeString = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
            hour12: true,
        });
        time = `${dateString} at ${timeString}`;
    } else {
        // Show relative time for scheduled/published posts (for ALL post types including Lead Generation)
        const diffMs = Date.now() - scheduledDate.getTime();
        const diffMin = Math.floor(Math.abs(diffMs) / (1000 * 60));
        if (diffMs >= 0) {
            // Post time is in the past
            if (diffMin < 60) {
                time = `${diffMin} m`;
            } else {
                const diffHr = Math.floor(diffMin / 60);
                if (diffHr < 24) {
                    time = `${diffHr} hr`;
                } else {
                    const diffDays = Math.floor(diffHr / 24);
                    time = `${diffDays} d`;
                }
            }
        } else {
            // Post time is in the future
            if (diffMin < 60) {
                time = `in ${diffMin} m`;
            } else if (diffMin < 1440) {
                const diffHr = Math.floor(diffMin / 60);
                time = `in ${diffHr} hr`;
            } else {
                const diffDays = Math.floor(diffMin / 1440);
                time = `in ${diffDays} d`;
            }
        }
    }

    const isMobile = useIsMobile(900);

    return (
        <div className="relative max-w-xl mx-auto rounded-lg border border-gray-200 overflow-hidden">
            {/* Post Container */}
            <div className="bg-white shadow-sm">
                <div className="p-4 border-b border-gray-200">
                    {/* Header */}
                    <div className="flex items-start mb-2">
                        {post?.userImage && post.userImage.trim() !== "" ? (
                            <img
                                src={
                                    post?.userImage &&
                                    post.userImage.trim() !== ""
                                        ? post.userImage
                                        : undefined
                                }
                                width={40}
                                height={40}
                                alt="Profile"
                                className="rounded-full object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                        ) : null}
                        <span 
                            className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                            style={{ display: post?.userImage && post.userImage.trim() !== "" ? 'none' : 'flex' }}
                        >
                            {user && user.nameFirst && user.nameLast
                                ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                                : post?.displayName 
                                ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                : 'U'}
                        </span>
                        <div className="ml-3 flex-1">
                            <p className="font-semibold text-sm">
                                {post?.displayName
                                    ? post.displayName
                                    : `${user?.nameFirst} ${user?.nameLast}`}
                            </p>
                            <p className="text-xs text-gray-500">{!hideDatetime && `${time}`}</p>
                        </div>
                    </div>

                    {/* Combined Post Text and In-Post Footer */}
                    {/* Don't render content text if using background colors (it's rendered in renderMedia) */}
                    {
                    // !(post?.background && post?.textColor) &&
                    // (
                        <div className="mt-2 mb-3">
                            {((post as any)?.contentType === "Caption Video" || (post as any)?.type === "Caption Video") &&
                            !post?.content &&
                            (post?.status?.toLowerCase() === "draft" || post?.status?.toLowerCase() === "videoprocessing") ? (
                                <div className="space-y-2 py-1">
                                    <div className="h-3 bg-gray-200 rounded w-3/4 shimmer-bar" />
                                    <div className="h-3 bg-gray-200 rounded w-1/2 shimmer-bar" />
                                </div>
                            ) : (
                            <p className="text-sm whitespace-pre-line break-words overflow-hidden"
                            style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}
                            >
                                {(showFullText || !isTruncated
                                    ? combinedText
                                    : truncatedText
                                )
                                    .split("\n")
                                    .map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            <br />
                                        </React.Fragment>
                                    ))}
                                {(showFullText || !isTruncated) && overflowContent && (
                                    <span className="bg-red-100 line-through text-red-600">{overflowContent}</span>
                                )}
                                {isTruncated && (
                                    <span
                                        className="text-gray-500 cursor-pointer"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setShowFullText(!showFullText);
                                        }}
                                    >
                                        {showFullText ? " less" : " more"}
                                    </span>
                                )}
                            </p>
                            )}
                        </div>
                    // )
                    }

                    {/* Media Section */}
                    {((post?.media && post.media.length > 0) || (post?.background && post?.textColor)) && (
                        <div className="mb-3">{renderMedia(post.media)}</div>
                    )}

                    {/* Post Actions */}
                    <div
                        className={`border-t border-gray-200 w-full ${
                            isMobile ? "pt-4" : "pt-1"
                        }`}
                    >
                        <div className="flex justify-between">
                            <Button
                                variant="ghost"
                                size="sm"
                                className={`flex-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                            >
                                <ThumbsUp
                                    className={`h-5 w-5 ${
                                        isMobile ? "mb-1" : "mr-1"
                                    }`}
                                />
                                {!isMobile && <span>Like</span>}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={`flex-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                                onClick={() => setShowComments(!showComments)}
                            >
                                <MessageSquare
                                    className={`h-5 w-5 ${
                                        isMobile ? "mb-1" : "mr-1"
                                    }`}
                                />
                                {!isMobile && <span>Comment</span>}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={`flex-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                            >
                                <Repeat2
                                    className={`h-5 w-5 ${
                                        isMobile ? "mb-1" : "mr-1"
                                    }`}
                                />
                                {!isMobile && <span>Repost</span>}
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className={`flex-1 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700`}
                            >
                                <Send
                                    className={`h-5 w-5 ${
                                        isMobile ? "mb-1" : "mr-1"
                                    }`}
                                />
                                {!isMobile && <span>Send</span>}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            {/* Comment Footer (if footerType is "comment") */}
            {post?.footerType === "comment" && post?.includeFooter && post?.footer && (
                <div className="max-w-xl mx-auto mt-0 bg-white ">
                    <div className="p-3 flex items-start gap-3">
                            <img
                                src={
                                    post?.userImage ||
                                    user?.photoURL ||
                                    '/placeholder-avatar.svg'
                                }
                                alt="Profile"
                                width={40}
                                height={40}
                                className="rounded-full object-cover"
                                onError={(e) => {
                                    const target = e.target as HTMLImageElement;
                                    target.style.display = 'none';
                                    const fallback = target.nextElementSibling as HTMLElement;
                                    if (fallback) fallback.style.display = 'flex';
                                }}
                            />
                            <span 
                                className="text-sm font-semibold flex items-center justify-center w-10 h-10 min-w-[40px] min-h-[40px] bg-gray-300 rounded-full"
                                style={{ display: (post?.userImage || user?.photoURL) ? 'none' : 'flex' }}
                            >
                                {user && user.nameFirst && user.nameLast
                                    ? user.nameFirst[0].toUpperCase() + user.nameLast[0].toUpperCase()
                                    : post?.displayName 
                                    ? post.displayName.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
                                    : 'U'}
                            </span>
                        <div className="flex-1 min-w-0 overflow-hidden">
                            <div className="flex justify-between items-start">
                                <div>
                                    <p className="font-semibold text-sm">
                                        {post?.displayName
                                            ? post.displayName
                                            : `${user?.nameFirst || "John"} ${
                                                  user?.nameLast || "Cricket"
                                              }`}
                                    </p>
                                </div>
                                <div className="flex items-center justify-end gap-1">
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                        {!hideDatetime && time}{" "}
                                        {!hideDatetime && (
                                            <span className="text-[4px] mx-0.5">
                                                •
                                            </span>
                                        )}
                                    </p>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8"
                                    >
                                        <MoreHorizontal className="h-4 w-4 text-neutral-500" />
                                    </Button>
                                </div>
                            </div>
                            <div className="mt-1 text-sm text-gray-900 whitespace-pre-line break-words overflow-hidden" style={{ wordBreak: "break-word", overflowWrap: "anywhere" }}>
                                {(
                                    post.footer ||
                                    "I have never understood how anyone can be satisfied building a product they don't understand.\n\nOn a different note, the image is flawed, Roman roads didn't have to withstand heavy trucks. Plus they weren't built by the lowest bidder."
                                )
                                    .split("\n")
                                    .map((line, index) => (
                                        <React.Fragment key={index}>
                                            {line}
                                            {index <
                                                (post.footer || "").split("\n")
                                                    .length -
                                                    1 && <br />}
                                        </React.Fragment>
                                    ))}
                            </div>
                            {!isMobile && (
                                <div className="mt-2 pt-1 border-t border-gray-100 flex items-center text-sm text-gray-500">
                                    <div className="flex items-center gap-1">
                                        <span className="text-sm">Like</span>
                                        <span className="text-[4px] mx-0.5">
                                            •
                                        </span>
                                        <div className="flex items-center">
                                            <div className="flex -space-x-1">
                                                <div className="h-4 w-4 rounded-full bg-blue-500 flex items-center justify-center border border-white">
                                                    <ThumbsUp className="h-2 w-2 text-white" />
                                                </div>
                                                <div className="h-4 w-4 rounded-full bg-green-500 flex items-center justify-center border border-white">
                                                    <ThumbsUp className="h-2 w-2 text-white" />
                                                </div>
                                                <div className="h-4 w-4 rounded-full bg-red-500 flex items-center justify-center border border-white">
                                                    <ThumbsUp className="h-2 w-2 text-white" />
                                                </div>
                                            </div>
                                            <span className="ml-1.5">132</span>
                                        </div>
                                        <span className="text-[4px] mx-0.5">
                                            •
                                        </span>
                                        <span>Reply</span>
                                        <span className="text-[4px] mx-0.5">
                                            •
                                        </span>
                                        <span>12 Replies</span>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* Generic Media Gallery Overlay */}
            {showGallery && (
                <FacebookMediaGalleryOverlay
                    media={post?.media || []}
                    initialIndex={galleryInitialIndex}
                    onClose={() => setShowGallery(false)}
                />
            )}
        </div>
    );
};

export default LinkedInPost;
